import { ITooltipOption } from '../../../../../components/KubitTooltip/globalTooltips';
import {
  SignificanceModeKey,
  SignificanceTestKey,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../../../utils/formatting';

export const significanceModeTooltips: Record<
  SignificanceModeKey,
  ITooltipOption
> = {
  [SignificanceModeKey.Breakdown]: {
    content: `Compare the impact of a treatment (variant field value) against a 
    baseline (control field value).`,
  },
  [SignificanceModeKey.Experiment]: {
    content: `Compare the impact of an experiment treatment (variant) against a 
    baseline (control).`,
  },
  [SignificanceModeKey.Segment]: {
    content: `Compare the impact of a treatment (variant segment) against a 
    baseline (control segment).`,
  },
};

export const significanceBuilderCriteriaInfo: ITooltipOption = {
  content: (
    <p>
      Compare the <strong>Control</strong> and <strong>Variant</strong> groups
      and determine if the difference is statistically significant.
    </p>
  ),
};
export const significanceBuilderMetricInfo: ITooltipOption = {
  content: `Select a metric to assess the performance or outcome.`,
};

export const significanceTestInfo: ITooltipOption = {
  content: (
    <p>
      <strong>One-Tail Test</strong>: Looks for an effect in one direction
      (e.g., better or worse).
      <br />
      <br />
      <strong>Two-Tail Test</strong>: Looks for an effect in both directions
      (e.g., different, whether better or worse).
      <br />
      <br />
      Use a one-tail test for a specific direction and a two-tail test for any
      difference.
    </p>
  ),
};
export const significanceTestOptionsInfo: Record<
  SignificanceTestKey,
  ITooltipOption
> = {
  [SignificanceTestKey.OneTailed]: {
    content: 'Looks for an effect in one direction (e.g., better or worse).',
  },
  [SignificanceTestKey.TwoTailed]: {
    content:
      'Looks for an effect in both directions (e.g., different, whether better or worse).',
  },
};
export const significancePValueInfo: ITooltipOption = {
  content: (
    <p>
      <strong>Description</strong>: The desired p-value threshold to decide if
      the null hypothesis is true.
      <br />
      <br />
      <strong>Range</strong>: It goes from{' '}
      {globalFormat.formatNumber(
        '0.01',
        defaultNumberFormatOptions.originDecimalPoint,
      )}{' '}
      to{' '}
      {globalFormat.formatNumber(
        '0.1',
        defaultNumberFormatOptions.originDecimalPoint,
      )}
      .
      <br />
      <br />
      <strong>Interpretation</strong>: If the result p-value is less than the
      desired (e.g.{' '}
      {globalFormat.formatNumber(
        '0.05',
        defaultNumberFormatOptions.originDecimalPoint,
      )}
      ), it means our result is statistically significant, and we should
      consider rejecting the null hypothesis.
    </p>
  ),
};
export const significanceConfidenceLevelInfo: ITooltipOption = {
  content: (
    <p>
      <strong>Description</strong>: The confidence level indicates the desired
      likelihood that observed differences between <strong>Control</strong> and{' '}
      <strong>Variant</strong> are real, not random.
      <br />
      <br />
      <strong>Range</strong>: Typically given as a percentage (e.g., 95%
      confidence level).
      <br />
      <br />
      <strong>Interpretation</strong>: A 95% confidence level means we want to
      be 95% confident that the true <strong>Lift</strong> between{' '}
      <strong>Control</strong> and <strong>Variant</strong> lies within the
      calculated interval.
    </p>
  ),
};
export const significanceStErrorInfo: ITooltipOption = {
  content: (
    <p>
      <strong>Description</strong>: Measures how much the difference between two
      sample statistics (like proportions or means) from groups A and B might
      vary due to random chance.
      <br />
      <br />
      <strong>Range</strong>: Always positive and usually small, showing how
      much the sample differences can vary.
      <br />
      <br />
      <strong>Interpretation</strong>: Smaller SE means more precise estimates.
      Leads to narrower confidence intervals. If the 95% confidence interval
      does not include zero, the difference is statistically significant.
    </p>
  ),
};
